import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import MainNav from "@/components/MainNav";
import { FaSpotify, FaApple, FaInstagram, FaTiktok, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { getPromotionClients } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Generate initials for fallback avatar
const getInitials = (name) => {
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

// Featured Artist Card for Carousel
function FeaturedArtistCard({ name, index }) {
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/artist-image?name=${encodeURIComponent(name)}`);
        const data = await res.json();
        if (data.image) {
          setImage(data.image);
        }
      } catch (error) {
        console.error(`Error fetching image for ${name}:`, error);
      } finally {
        setLoading(false);
      }
    };
    fetchImage();
  }, [name]);

  const encodedName = encodeURIComponent(name);
  const spotifyUrl = `https://open.spotify.com/search/${encodedName}`;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      className="flex-shrink-0 w-64 md:w-72"
    >
      <a
        href={spotifyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block group"
      >
        <div className="relative aspect-square rounded-xl overflow-hidden bg-zinc-900 border border-zinc-700 group-hover:border-zinc-500 transition-all">
          {loading ? (
            <div className="w-full h-full animate-pulse bg-zinc-800" />
          ) : image ? (
            <img
              src={image}
              alt={name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-700 to-zinc-900">
              <span className="text-4xl font-bold text-zinc-500">
                {getInitials(name)}
              </span>
            </div>
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          {/* Name overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="text-lg font-bold text-white truncate">{name}</h3>
            <p className="text-xs text-zinc-400">Featured Artist</p>
          </div>
        </div>
      </a>
    </motion.div>
  );
}

// Carousel Component
function FeaturedCarousel({ artists }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      return () => ref.removeEventListener('scroll', checkScroll);
    }
  }, [artists]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (artists.length === 0) return null;

  return (
    <div className="relative mb-16">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-syne font-bold text-white">Featured Artists</h2>
        <div className="flex gap-2">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
              canScrollLeft 
                ? 'border-zinc-600 text-white hover:bg-zinc-800' 
                : 'border-zinc-800 text-zinc-700 cursor-not-allowed'
            }`}
          >
            <FaChevronLeft size={14} />
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
              canScrollRight 
                ? 'border-zinc-600 text-white hover:bg-zinc-800' 
                : 'border-zinc-800 text-zinc-700 cursor-not-allowed'
            }`}
          >
            <FaChevronRight size={14} />
          </button>
        </div>
      </div>
      
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {artists.map((artist, index) => (
          <FeaturedArtistCard key={artist.id || index} name={artist.name} index={index} />
        ))}
      </div>
    </div>
  );
}

// Artist card component with image fetching
function ArtistCard({ name, index }) {
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/artist-image?name=${encodeURIComponent(name)}`);
        const data = await res.json();
        if (data.image) {
          setImage(data.image);
        }
      } catch (error) {
        console.error(`Error fetching image for ${name}:`, error);
      } finally {
        setLoading(false);
      }
    };
    fetchImage();
  }, [name]);

  const encodedName = encodeURIComponent(name);
  const spotifyUrl = `https://open.spotify.com/search/${encodedName}`;
  const appleUrl = `https://music.apple.com/search?term=${encodedName}`;
  const instagramUrl = `https://www.instagram.com/explore/search/keyword/?q=${encodedName}`;
  const tiktokUrl = `https://www.tiktok.com/search?q=${encodedName}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.02, 0.5) }}
      className="group"
      data-testid={`promotion-client-${index}`}
    >
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 hover:border-zinc-600 hover:bg-zinc-800/30 transition-all">
        {/* Artist Image */}
        <div className="w-20 h-20 mx-auto mb-3 rounded-full overflow-hidden bg-zinc-800">
          {loading ? (
            <div className="w-full h-full animate-pulse bg-zinc-700" />
          ) : image ? (
            <img
              src={image}
              alt={name}
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-700 to-zinc-800">
              <span className="text-lg font-bold text-zinc-400">
                {getInitials(name)}
              </span>
            </div>
          )}
        </div>
        
        {/* Name */}
        <h3 className="text-sm font-medium text-white text-center mb-3 truncate">
          {name}
        </h3>
        
        {/* Social Links */}
        <div className="flex items-center justify-center gap-2">
          <a
            href={spotifyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-green-600 flex items-center justify-center transition-colors"
            title="Search on Spotify"
          >
            <FaSpotify className="w-4 h-4 text-zinc-400 hover:text-white" />
          </a>
          <a
            href={appleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-pink-600 flex items-center justify-center transition-colors"
            title="Search on Apple Music"
          >
            <FaApple className="w-4 h-4 text-zinc-400 hover:text-white" />
          </a>
          <a
            href={instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-purple-600 flex items-center justify-center transition-colors"
            title="Search on Instagram"
          >
            <FaInstagram className="w-4 h-4 text-zinc-400 hover:text-white" />
          </a>
          <a
            href={tiktokUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-600 flex items-center justify-center transition-colors"
            title="Search on TikTok"
          >
            <FaTiktok className="w-4 h-4 text-zinc-400 hover:text-white" />
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function PromotionPage() {
  const [clients, setClients] = useState([]);
  const [featuredArtists, setFeaturedArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await getPromotionClients();
        const allClients = res.data || [];
        
        // Sort alphabetically
        const sorted = allClients.sort((a, b) => 
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
        
        setClients(sorted);
        
        // Featured artists - use those marked as featured, fallback to first 8
        const featured = sorted.filter(c => c.featured);
        setFeaturedArtists(featured.length > 0 ? featured : sorted.slice(0, 8));
      } catch (error) {
        console.error("Error fetching promotion clients:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="promotion-page">
      <MainNav />

      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-3xl md:text-4xl font-syne font-bold text-white" data-testid="promotion-title">Promotion</h1>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[1,2,3,4,5,6,7,8].map(i => (
                <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 animate-pulse">
                  <div className="w-20 h-20 mx-auto mb-3 rounded-full bg-zinc-800" />
                  <div className="h-4 bg-zinc-800 rounded mx-auto w-24 mb-3" />
                  <div className="flex justify-center gap-2">
                    {[1,2,3,4].map(j => (
                      <div key={j} className="w-8 h-8 rounded-full bg-zinc-800" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-zinc-500">No promotion clients yet.</p>
            </div>
          ) : (
            <>
              {/* Featured Carousel */}
              <FeaturedCarousel artists={featuredArtists} />
              
              {/* All Artists Section */}
              <div>
                <h2 className="text-xl md:text-2xl font-syne font-bold text-white mb-6">All Artists</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="promotion-clients-grid">
                  {clients.map((client, index) => (
                    <ArtistCard key={client.id || index} name={client.name} index={index} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <footer className="border-t border-zinc-800 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-zinc-600 text-xs">© 2025 Underplay. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
